import React, { createContext, useContext, useReducer, useEffect } from "react";
import api, { API_CONFIG } from "../../../../utils/apiConfig";
import apiCache from "../../../../utils/apiCache";

// Initial state
const initialState = {
  user: null,
  token: localStorage.getItem("b2bToken"),
  isAuthenticated: false,
  isLoading: true,
  error: null,
  profileCompleted: false,
  emailVerified: false,
};

// Action types
const AUTH_ACTIONS = {
  LOGIN_START: "LOGIN_START",
  LOGIN_SUCCESS: "LOGIN_SUCCESS",
  LOGIN_FAILURE: "LOGIN_FAILURE",
  LOGOUT: "LOGOUT",
  REGISTER_START: "REGISTER_START",
  REGISTER_SUCCESS: "REGISTER_SUCCESS",
  REGISTER_FAILURE: "REGISTER_FAILURE",
  CLEAR_ERROR: "CLEAR_ERROR",
  SET_LOADING: "SET_LOADING",
  UPDATE_PROFILE_STATUS: "UPDATE_PROFILE_STATUS",
  EMAIL_VERIFIED: "EMAIL_VERIFIED",
  RESEND_VERIFICATION: "RESEND_VERIFICATION",
};

// Reducer
const authReducer = (state: any, action: any) => {
  switch (action.type) {
    case AUTH_ACTIONS.LOGIN_START:
    case AUTH_ACTIONS.REGISTER_START:
      return {
        ...state,
        isLoading: true,
        error: null,
      };

    case AUTH_ACTIONS.LOGIN_SUCCESS:
    case AUTH_ACTIONS.REGISTER_SUCCESS:
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        isAuthenticated: true,
        isLoading: false,
        error: null,
        profileCompleted: action.payload.profileCompleted || false,
        emailVerified: action.payload.emailVerified || false,
      };

    case AUTH_ACTIONS.LOGIN_FAILURE:
    case AUTH_ACTIONS.REGISTER_FAILURE:
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
        error: action.payload,
        profileCompleted: false,
        emailVerified: false,
      };

    case AUTH_ACTIONS.LOGOUT:
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
        profileCompleted: false,
        emailVerified: false,
      };

    case AUTH_ACTIONS.CLEAR_ERROR:
      return {
        ...state,
        error: null,
      };

    case AUTH_ACTIONS.SET_LOADING:
      return {
        ...state,
        isLoading: action.payload,
      };

    case AUTH_ACTIONS.UPDATE_PROFILE_STATUS:
      return {
        ...state,
        profileCompleted: action.payload,
      };

    case AUTH_ACTIONS.EMAIL_VERIFIED:
      return {
        ...state,
        emailVerified: true,
        user: {
          ...state.user,
          isEmailVerified: true,
        },
      };

    case AUTH_ACTIONS.RESEND_VERIFICATION:
      return {
        ...state,
      };

    default:
      return state;
  }
};

// Create context with proper typing and default values
const B2BAuthContext = createContext<any>({});

// Provider component
export const B2BAuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Check if user is authenticated on mount
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem("b2bToken");
      const user = localStorage.getItem("b2bUser");
      
      if (token && user) {
        try {
          // Parse user data from localStorage
          const userData = JSON.parse(user);
          
          // Use the profileComplete field from stored user data
          const profileCompleted = userData?.profileComplete || false;
          const emailVerified = userData?.isEmailVerified || false;
          
          dispatch({
            type: AUTH_ACTIONS.LOGIN_SUCCESS,
            payload: {
              user: userData,
              token,
              profileCompleted,
              emailVerified,
            },
          });
        } catch (error) {
          console.error("Error parsing user data:", error);
          localStorage.removeItem("b2bToken");
          localStorage.removeItem("b2bUser");
          dispatch({ type: AUTH_ACTIONS.LOGOUT });
        }
      } else {
        dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
      }
    };

    checkAuth();
  }, []);

  // Login function - ENTERPRISE STANDARD STRUCTURE
  const login = async (email: string, password: string, rememberMe: boolean = false) => {
    // Auto-logout any existing user before new login attempt
    if (state.isAuthenticated) {
      // Clear existing session
      localStorage.removeItem('b2bToken');
      localStorage.removeItem('b2bUser');
      dispatch({ type: AUTH_ACTIONS.LOGOUT });
      
      // Add delay to let rate limit reset after auto-logout
      await new Promise(resolve => setTimeout(resolve, 5000)); // 5 second delay
    }
    
    dispatch({ type: AUTH_ACTIONS.LOGIN_START });

    // Brief delay to prevent rapid-fire requests
    await new Promise(resolve => setTimeout(resolve, 1000)); // 1 second delay

    try {
      const response = await api.login({ email, password, rememberMe });

      // Handle pending approval case - backend returns success: false with code PENDING_APPROVAL
      if (!response.success && response.code === 'PENDING_APPROVAL' && response.data) {
        const token = response.data.token;
        const user = response.data.user;
        
        if (token && user) {
          localStorage.setItem("b2bToken", token);
          localStorage.setItem("b2bUser", JSON.stringify(user));
          
          // Set as authenticated but not approved
          dispatch({
            type: AUTH_ACTIONS.LOGIN_SUCCESS,
            payload: {
              user: user,
              token: token,
              profileCompleted: user?.profileComplete || false,
              emailVerified: user?.isEmailVerified || false,
            },
          });
          return; // Exit early, don't throw error
        }
      }

      // Handle email not verified case
      if (!response.success && response.code === 'EMAIL_NOT_VERIFIED') {
        const e = new Error(response.error || "Please verify your email address before logging in");
        (e as any).code = 'EMAIL_NOT_VERIFIED';
        throw e;
      }

      // FIXED: Now using proper enterprise standard structure
      const token = response.data?.token;
      const user = response.data?.user;

      if (!token || !user) {
        throw new Error("Invalid response structure from login API");
      }

      // Store token and user data
      localStorage.setItem("b2bToken", token);
      localStorage.setItem("b2bUser", JSON.stringify(user));
      apiCache.clearCache("/api/b2b/users/profile");

      // Check profile completion status
      const profileCompleted = user?.profileComplete || false;
      const emailVerified = (user as any)?.isEmailVerified || false;

      dispatch({
        type: AUTH_ACTIONS.LOGIN_SUCCESS,
        payload: {
          user: user,
          token: token,
          profileCompleted,
          emailVerified,
        },
      });
    } catch (error) {
      // Handle pending approval case from error response (fallback)
      if ((error as any).response?.data?.code === 'PENDING_APPROVAL') {
        // Check if we have user data in the error response
        const userData = (error as any).response?.data?.user;
        const token = (error as any).response?.data?.token;
        
        if (userData && token) {
          localStorage.setItem("b2bToken", token);
          localStorage.setItem("b2bUser", JSON.stringify(userData));
          apiCache.clearCache("/api/b2b/users/profile");

          // Set as authenticated but not approved
          dispatch({
            type: AUTH_ACTIONS.LOGIN_SUCCESS,
            payload: {
              user: userData,
              token: token,
              profileCompleted: userData?.profileComplete || false,
              emailVerified: userData?.isEmailVerified || false,
            },
          });
          
          // Still throw the error so the login form can handle the redirect
          throw error;
        }
      }
      
      dispatch({ type: AUTH_ACTIONS.LOGIN_FAILURE, payload: (error as any).message });
      throw error;
    }
  };

  // Register function - ENTERPRISE STANDARD STRUCTURE
  const register = async (userData: any) => {
    dispatch({ type: AUTH_ACTIONS.REGISTER_START });

    try {
      const response = await api.register(userData);

      // FIXED: Now using proper enterprise standard structure
      const token = response.data?.token;
      const user = response.data?.user;

      if (!token || !user) {
        throw new Error("Invalid response structure from register API");
      }

      // Store token and user data
      localStorage.setItem("b2bToken", token);
      localStorage.setItem("b2bUser", JSON.stringify(user));

      dispatch({
        type: AUTH_ACTIONS.REGISTER_SUCCESS,
        payload: {
          user: user,
          token: token,
          profileCompleted: false,
          emailVerified: false,
        },
      });
    } catch (error) {
      dispatch({ type: AUTH_ACTIONS.REGISTER_FAILURE, payload: (error as any).message });
      throw error;
    }
  };

  // Logout function
  const logout = () => {
    localStorage.removeItem("b2bToken");
    localStorage.removeItem("b2bUser");
    // Note: We keep remembered credentials on logout so user can log back in easily
    // If you want to clear them on logout, uncomment the lines below:
    // localStorage.removeItem("b2bRememberedEmail");
    // localStorage.removeItem("b2bRememberedPassword");
    dispatch({ type: AUTH_ACTIONS.LOGOUT });
  };

  // Clear error function
  const clearError = () => {
    dispatch({ type: AUTH_ACTIONS.CLEAR_ERROR });
  };

  // Update profile completion status
  const updateProfileStatus = (completed: boolean) => {
    dispatch({ type: AUTH_ACTIONS.UPDATE_PROFILE_STATUS, payload: completed });
  };

  // Check if user is admin
  const isAdmin = () => {
    return state.user?.isAdmin || false;
  };

  // Check if user has completed profile
  const hasCompletedProfile = async () => {
    // If user is not authenticated, return false without making API calls
    if (!state.isAuthenticated || !state.user) {
      return false;
    }

    // If we have a cached result and user hasn't changed, use it
    if (state.user?.profileComplete) {
      return true;
    }

    try {
      // Check profile completion status from server
      const response = await api.checkProfileCompletion();
      
      // The backend returns { success: true, data: { isComplete: boolean, ... } }
      if (response.data?.data?.isComplete) {
        // Update the user's profile completion status
        dispatch({ 
          type: AUTH_ACTIONS.UPDATE_PROFILE_STATUS, 
          payload: true 
        });
        return true;
      }
      return false;
    } catch (error) {
      console.error("Error checking profile completion:", error);
      // Fallback to local state
      return state.user?.profileComplete || state.profileCompleted || false;
    }
  };

  // Check if user is approved - FIXED LOGIC
  const isApproved = () => {
    // Admin users are automatically approved
    if (state.user?.isAdmin) {
      return true;
    }
    // Regular users need explicit approval
    return state.user?.isApproved || false;
  };

  // Email verification function
  const verifyEmail = async (token: string) => {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
      
      const response = await fetch(`${API_CONFIG.B2B_API_URL}/api/b2b/auth/verify-email/${token}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        signal: controller.signal,
      });

      clearTimeout(timeoutId);
      const data = await response.json();
      
      if (response.ok && data.success) {
        // Backend now returns a token + user on successful verification
        // so the user is automatically logged in (even in a fresh browser tab)
        if (data.data?.token && data.data?.user) {
          localStorage.setItem("b2bToken", data.data.token);
          localStorage.setItem("b2bUser", JSON.stringify(data.data.user));

          dispatch({
            type: AUTH_ACTIONS.LOGIN_SUCCESS,
            payload: {
              user: data.data.user,
              token: data.data.token,
              profileCompleted: data.data.user.profileComplete || false,
              emailVerified: true,
            },
          });
        } else {
          // Fallback: update existing localStorage entry
          const currentUser = JSON.parse(localStorage.getItem("b2bUser") || "{}");
          const updatedUser = { ...currentUser, isEmailVerified: true };
          localStorage.setItem("b2bUser", JSON.stringify(updatedUser));

          dispatch({
            type: AUTH_ACTIONS.EMAIL_VERIFIED,
          });
        }
        
        return { success: true, message: data.message };
      } else {
        // Handle specific backend error codes
        if (response.status === 400 && data.message?.includes('already verified')) {
          // Email is already verified, treat as success
          const currentUser = JSON.parse(localStorage.getItem("b2bUser") || "{}");
          const updatedUser = { ...currentUser, isEmailVerified: true };
          localStorage.setItem("b2bUser", JSON.stringify(updatedUser));

          dispatch({
            type: AUTH_ACTIONS.EMAIL_VERIFIED,
          });
          
          return { success: true, message: "Email is already verified" };
        }
        
        const error = new Error(data.message || "Email verification failed") as any;
        error.response = { status: response.status, data };
        throw error;
      }
    } catch (error) {
      // Handle network errors more gracefully
      if (error.name === 'AbortError') {
        const timeoutError = new Error('Request timeout - please check your connection and try again') as any;
        timeoutError.response = { status: 408 };
        throw timeoutError;
      }
      
      // Re-throw with response data if available
      throw error;
    }
  };

  // Resend verification email
  const resendVerification = async (email: string) => {
    try {
      const response = await fetch(`${API_CONFIG.B2B_API_URL}/api/b2b/auth/resend-verification`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();
      
      if (response.ok && data.success) {
        dispatch({
          type: AUTH_ACTIONS.RESEND_VERIFICATION,
        });
        return data;
      } else {
        // Create an error object that includes the response for proper error handling
        const error = new Error(data.message || "Failed to resend verification email");
        (error as any).response = {
          ...response,
          data: data,
          status: response.status
        };
        throw error;
      }
    } catch (error: any) {
      // If it's already an error with response, just rethrow
      if (error.response) {
        throw error;
      }
      // Otherwise, wrap it
      const wrappedError = new Error(error.message || "Failed to resend verification email");
      (wrappedError as any).response = {
        status: 500,
        data: { message: error.message || "Network error occurred" }
      };
      throw wrappedError;
    }
  };

  // Check if email is verified
  const isEmailVerified = () => {
    return state.emailVerified || state.user?.isEmailVerified || false;
  };

  // Refresh user data from backend
  const refreshUserData = async () => {
    const token = localStorage.getItem("b2bToken");
    if (!token) {
      return;
    }

    try {
      const response = await fetch(`${API_CONFIG.B2B_API_URL}/api/b2b/auth/me`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        
        if (data.success && data.data && data.data.user) {
          const userData = data.data.user;
          // Update localStorage with fresh user data
          localStorage.setItem("b2bUser", JSON.stringify(userData));
          
          // Update React state with fresh user data
          dispatch({
            type: AUTH_ACTIONS.LOGIN_SUCCESS,
            payload: {
              user: userData,
              token: token,
            },
          });
        } else {
          console.error("Invalid response structure:", data);
        }
      } else if (response.status === 429) {
        // Don't throw error for rate limiting, just return
        return;
      } else {
        console.error("Failed to refresh user data:", response.status, response.statusText);
      }
    } catch (error) {
      console.error("Error refreshing user data:", error);
    }
  };

  // Manually initialize authentication from localStorage (for cases where context isn't properly initialized)
  const initializeAuthFromStorage = () => {
    const token = localStorage.getItem("b2bToken");
    const userData = localStorage.getItem("b2bUser");
    
    if (token && userData) {
      try {
        const parsedUser = JSON.parse(userData);
        
        dispatch({
          type: AUTH_ACTIONS.LOGIN_SUCCESS,
          payload: {
            user: parsedUser,
            token: token,
            profileCompleted: parsedUser?.profileComplete || false,
            emailVerified: parsedUser?.isEmailVerified || false,
          },
        });
        
        return true;
      } catch (error) {
        console.error("Error initializing auth from localStorage:", error);
        return false;
      }
    }
    return false;
  };

  const value = {
    ...state,
    login,
    register,
    logout,
    clearError,
    updateProfileStatus,
    verifyEmail,
    resendVerification,
    refreshUserData,
    initializeAuthFromStorage,
    isAdmin,
    hasCompletedProfile,
    isApproved,
    isEmailVerified,
  };

  return (
    <B2BAuthContext.Provider value={value}>{children}</B2BAuthContext.Provider>
  );
};

// Custom hook to use auth context
export const useB2BAuth = () => {
  const context = useContext(B2BAuthContext);
  if (!context) {
    throw new Error("useB2BAuth must be used within a B2BAuthProvider");
  }
  return context;
};